import { useEffect } from 'react';
import { TeamHierarchyDto } from '@act/shared/data-transfer-objects';
import {
  getTeam,
  selectHierarchy,
  selectTeamLoadingStatus,
  selectViewable,
} from 'redux/slices/usersSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import useIsAuthenticated from 'redux/utils/auth/is-authenticated-hook';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { useExponentialBackoff } from 'utilities/data/useExponentialBackoff';

const InitialDataHelper = () => {
  const hierarchy: TeamHierarchyDto[] = useAppSelector(selectHierarchy);
  const viewableGuides = useAppSelector(selectViewable);
  const teamLoadingStatus = useAppSelector(selectTeamLoadingStatus);
  const isAuthenticated = useIsAuthenticated();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const dispatch = useAppDispatch();

  // Exponential backoff: 1s, 2s, 4s, 8s (4 retries total)
  const {
    scheduleRetry,
    resetAttempts,
    clearPendingRetry,
    hasReachedMaxAttempts,
  } = useExponentialBackoff({
    maxRetryAttempts: 4,
    jitterMs: 100,
  });

  // Team Members ==============================================================
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!actingUser?.hasAppAccess) return; // Don't load data if user doesn't have app access

    // Clear any pending retry timeout when conditions change
    clearPendingRetry();

    // If we have data, reset retry tracking
    if (hierarchy?.length || viewableGuides !== null) {
      resetAttempts();
      return;
    }

    // If currently loading, don't do anything
    if (teamLoadingStatus === 'loading') {
      return;
    }

    // If it failed, schedule a retry with exponential backoff
    if (teamLoadingStatus === 'failed') {
      // Check if we've exceeded max retry attempts
      if (hasReachedMaxAttempts()) {
        // Stop retrying after max attempts
        return;
      }

      // Schedule the retry
      scheduleRetry(() => {
        dispatch(getTeam({ params: { allViewable: true } }));
      });
      return;
    }

    // Initial load or retry after success (status is 'initial' or 'success' but no data)
    if (teamLoadingStatus === 'initial' || teamLoadingStatus === 'success') {
      resetAttempts(); // Reset attempt counter on initial load or after success
      dispatch(getTeam({ params: { allViewable: true } }));
    }
  }, [
    dispatch,
    hierarchy,
    isAuthenticated,
    teamLoadingStatus,
    viewableGuides,
    actingUser?.hasAppAccess,
    scheduleRetry,
    resetAttempts,
    clearPendingRetry,
    hasReachedMaxAttempts,
  ]);

  return <></>;
};

export default InitialDataHelper;
